import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, CardContent, CircularProgress, Tab, Tabs } from "@mui/material";

import TurmaCertificadoPesquisaForm from "components/molecules/TurmaCertificadoPesquisaForm/TurmaCertificadoPesquisaForm";
import TurmaCriteriosForm from "components/molecules/TurmaCriteriosForm/TurmaCriteriosForm";
import GestaoVagasTurma from "components/molecules/GestaoVagasTurma/GestaoVagasTurma";
import TurmaInformacoesForm from "components/molecules/TurmaInformacoesForm/TurmaInformacoesForm";
import TurmaPublicoForm from "components/molecules/TurmaPublicoForm/TurmaPublicoForm";
import TurmaResponsaveisForm from "components/molecules/TurmaResponsaveisForm/TurmaResponsaveisForm";
import { useDadoComplementarStore } from "store/useDadoComplementarStore";
import { useDadosComplementares } from "hook/useDadosComplementares";
import { useTurmaSave } from "components/pages/Turmas/hooks/useTurmaSave";
import { useComplementoAcao } from "../AcoesQualificacao/hooks/useComplementoAcao";

import styles from "./Turmas.module.css";
import stylesButton from "styles/Button.module.css";
import stylesGeneral from "styles/General.module.css";
import stylesGrid from "styles/Grid.module.css";

const TAB_IDS = ["0", "1", "2", "3", "4"] as const;

const EditarTurma = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const turmaId = searchParams.get("id");

  const { loading, loadingSave, turma, acaoCurso, errors, saveTurma, handleInputChange, handleChange } =
    useTurmaSave(turmaId);

  const { loadingComplementos, modalidades } = useComplementoAcao();

  useDadosComplementares();
  const loadindDadosCompl = useDadoComplementarStore((state) => state.loadindDadosCompl);

  const [activeTab, setActiveTab] = useState<string>(TAB_IDS[0]);
  const [saveResponsaveisHandler, setSaveResponsaveisHandler] = useState<(() => Promise<boolean>) | null>(null);
  const [savingInformacoesCompletas, setSavingInformacoesCompletas] = useState(false);

  const handleStepSuccess = (currentStep: number) => {
    const nextStep = Math.min(currentStep + 1, TAB_IDS.length - 1);
    if (currentStep < TAB_IDS.length - 1) {
      setActiveTab(TAB_IDS[nextStep]);
    }
  };

  const handleSaveInformacoesCompletas = async () => {
    setSavingInformacoesCompletas(true);

    try {
      const turmaSalva = await saveTurma();
      if (!turmaSalva) {
        return;
      }

      if (saveResponsaveisHandler) {
        const responsaveisSalvos = await saveResponsaveisHandler();
        if (!responsaveisSalvos) {
          return;
        }
      }

      handleStepSuccess(0);
    } finally {
      setSavingInformacoesCompletas(false);
    }
  };

  const tabs = [
    {
      id: TAB_IDS[0],
      title: "Informações da Turma",
      content: (
        <>
          <TurmaInformacoesForm
            turma={turma}
            errors={errors}
            loadingSave={loadingSave || savingInformacoesCompletas}
            modalidades={modalidades}
            onInputChange={handleInputChange}
            onChangeField={handleChange}
            onSave={saveTurma}
            hideSaveButton
          />

          <div className={styles.editTurmaEmbeddedSection}>
            <div className={styles.editTurmaEmbeddedTitle}>Área ou Instituição responsável</div>
            <TurmaResponsaveisForm
              hideSaveButton
              onRegisterSave={(handler) => setSaveResponsaveisHandler(() => handler)}
            />
          </div>

          <div className={`${stylesGrid.inputGroupFull} ${stylesGrid.justifyCenter} ${stylesGeneral.mt6}`}>
            <Button
              variant="contained"
              onClick={handleSaveInformacoesCompletas}
              className={`${stylesButton.buttonPrimary} ${loadingSave || savingInformacoesCompletas ? stylesButton.buttonLoad : ""}`}
              color="primary"
              disabled={loadingSave || savingInformacoesCompletas}
            >
              {loadingSave || savingInformacoesCompletas ? (
                <>
                  <div className={stylesButton.spinner}></div>
                  <span>Salvando...</span>
                </>
              ) : (
                <span>Salvar dados da turma e responsáveis</span>
              )}
            </Button>
          </div>
        </>
      ),
    },
    {
      id: TAB_IDS[1],
      title: "Público da Turma",
      content: <TurmaPublicoForm onSaveSuccess={() => handleStepSuccess(1)} />,
    },
    {
      id: TAB_IDS[2],
      title: "Gestão de Vagas",
      content: <GestaoVagasTurma onSaveSuccess={() => handleStepSuccess(2)} />,
    },
    {
      id: TAB_IDS[3],
      title: "Critérios de aprovação da turma",
      content: <TurmaCriteriosForm onSaveSuccess={() => handleStepSuccess(3)} />,
    },
    {
      id: TAB_IDS[4],
      title: "Certificado e pesquisa de satisfação",
      content: <TurmaCertificadoPesquisaForm turma={turma} acaoCurso={acaoCurso} />,
    },
  ];

  const currentTab = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  if (loading || loadingComplementos || loadindDadosCompl) {
    return <CircularProgress />;
  }

  return (
    <Card className={styles.container}>
      <CardContent>
        <div className={styles.title}>Editar Turma</div>

        <div className={stylesGeneral.mt6}>
          <div>
            <div className={`${stylesGeneral.mt2} ${stylesGeneral.mb10}`}>
              <div className={styles.editTurmaTabsWrap}>
                <Tabs
                  value={activeTab}
                  onChange={(_event, value) => setActiveTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  className={styles.editTurmaTabs}
                >
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      value={tab.id}
                      label={tab.title}
                      className={styles.editTurmaTab}
                    />
                  ))}
                </Tabs>
              </div>

              <div className={styles.editTurmaTabPanel}>{currentTab.content}</div>
            </div>
          </div>

          <div className={`${stylesGrid.inputGroupFull} ${stylesGrid.justifyCenter} ${stylesGeneral.mt6}`}>
            <Button
              variant="contained"
              onClick={() => {
                if (turma?.acao_id) {
                  navigate(`/gestao-turmas?id=${turma.acao_id}`);
                  return;
                }
                navigate(-1);
              }}
              className={stylesButton.buttonSecondary}
              color="primary"
            >
              Voltar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EditarTurma;
